import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import useConfigurationForm from "./hooks/useConfigurationForm";
import ConfigurationModalHeader from "./ConfigurationModalHeader";
import ConfigurationTabs from "./ConfigurationTabs";
import ConnectionMethodSelector from "./ConnectionMethodSelector";
import ApiKeyConnection from "./connections/ApiKeyConnection";
import McpConnection from "./connections/McpConnection";
import OAuthConnection from "./connections/OAuthConnection";
import WebhookConnection from "./connections/WebhookConnection";
import SettingsTab from "./ui/SettingsTab";
import MonitoringTab from "./ui/MonitoringTab";
import { CONNECTION_METHODS, TABS } from "./constants/connectionMethods";
import {
  createIntegration,
  updateIntegration
} from "../../services/integrationService";

// Numeric coercion that preserves 0 and maps blank -> null (so a legitimate 0 or
// "no limit" is never silently reset to a default). numOr() keeps a default only
// for truly-empty non-nullable fields.
const numOrNull = (s) => {
  const t = (s ?? "").toString().trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};
const numOr = (s, dflt) => {
  const v = numOrNull(s);
  return v === null ? dflt : v;
};

export default function ConfigurationModal({
  service,
  integration,
  providerId,
  existingIntegration = null,
  supportedAuthTypes, // string[] of auth methods this API supports (from backend)
  supportedAuthType = "api_key", // legacy single-value fallback
  onClose
}) {
  const formState = useConfigurationForm(service, existingIntegration);

  // When creating, default the selected method to the first one the API supports.
  useEffect(() => {
    if (existingIntegration) return;
    const map = { api_key: "apikey", mcp: "mcp", oauth: "oauth", webhook: "webhook" };
    const ids = (supportedAuthTypes || []).map((t) => map[t]).filter(Boolean);
    if (ids.length && !ids.includes(formState.connectionMethod)) {
      formState.setConnectionMethod(ids[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedAuthTypes, existingIntegration]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isEditing = existingIntegration !== null;

  const handleConnect = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build the auth payload per connection method. secret_ref shape + base_url
      // MUST match the backend contract:
      //   api_key / mcp -> { api_key }   (mcp also sets base_url = server URL)
      //   oauth         -> whatever keys the API declares in credential_fields
      // On edit, a blank secret means "keep existing": we omit that key, and the
      // backend merges what we do send over the stored credentials.
      const method = formState.connectionMethod;
      const v = (s) => (s && s.trim() ? s.trim() : "");
      let secretRef; // undefined => omit (keep existing credential on edit)
      let baseUrl;
      let validationError = null;

      if (method === "mcp") {
        if (v(formState.mcpServerUrl)) baseUrl = v(formState.mcpServerUrl);
        else if (!isEditing) validationError = "MCP Server URL is required";
        if (v(formState.authToken)) secretRef = { api_key: v(formState.authToken) };
        else if (!isEditing)
          validationError = validationError || "Authentication Token is required";
      } else if (method === "oauth") {
        // Send only the credentials the user actually filled in. The backend
        // merges a partial secret_ref over the stored one, so a rotation no
        // longer has to restate every credential — and can no longer wipe the
        // ones this form didn't ask about.
        const keys = formState.oauthCredentialKeys || [];
        const filled = {};
        keys.forEach((k) => {
          const val = v(formState.oauthCredentials?.[k]);
          if (val) filled[k] = val;
        });

        if (Object.keys(filled).length) {
          secretRef = filled;
        }
        if (!isEditing) {
          const missing = keys.filter((k) => !filled[k]);
          if (missing.length) {
            validationError = `${missing
              .map((k) => k.replace(/_/g, " "))
              .join(", ")} ${missing.length === 1 ? "is" : "are"} required`;
          }
        }
      } else if (method === "webhook") {
        // Webhook integrations carry no secret here (managed via the webhook registry).
      } else if (method === "noauth") {
        // No authentication: no secret, no base_url, nothing to validate.
        // (Backend stores the no-auth sentinel; on edit secret_ref is omitted/preserved.)
      } else {
        // api_key (default)
        if (v(formState.apiEndpoint)) baseUrl = v(formState.apiEndpoint);
        if (v(formState.apiKey)) secretRef = { api_key: v(formState.apiKey) };
        else if (!isEditing) validationError = "API Key is required";
      }

      if (validationError) {
        setError(validationError);
        setLoading(false);
        return;
      }

      // Parse custom headers from string to object
      const customHeadersObj = {};
      if (formState.customHeaders) {
        formState.customHeaders.split("\n").forEach((line) => {
          // Split on the FIRST colon only — header values can contain ':'
          // (URLs, "Bearer a:b", base64 basic-auth, etc.).
          const idx = line.indexOf(":");
          if (idx <= 0) return;
          const key = line.slice(0, idx).trim();
          const value = line.slice(idx + 1).trim();
          if (key && value) {
            customHeadersObj[key] = value;
          }
        });
      }

      // Map connection method to auth_type
      const authTypeMap = {
        apikey: "api_key",
        mcp: "mcp",
        oauth: "oauth",
        webhook: "webhook",
        noauth: "no_auth"
      };

      // Prepare the integration data
      const integrationData = {
        provider_id: providerId, // UUID of the provider from navigation state
        provider_api_id: service.api_id, // UUID of the specific API/service
        auth_type: authTypeMap[formState.connectionMethod] || "api_key",
        ...(secretRef !== undefined ? { secret_ref: secretRef } : {}),
        ...(baseUrl ? { base_url: baseUrl } : {}),
        custom_headers: customHeadersObj, // Send as object (backend expects dict)
        request_timeout_ms: numOr(formState.requestTimeout, 30000),
        rate_limit_rpm: numOrNull(formState.rateLimit),
        max_retries: numOr(formState.maxRetries, 3),
        cache_ttl_seconds: numOrNull(formState.cacheTtl),
        enable_data_caching: formState.enableCaching,
        log_api_requests: formState.logRequests,
        enable_compression: formState.enableCompression,
        ssl_verify: formState.sslVerification,
        error_threshold: numOrNull(formState.errorThreshold),
        latency_threshold_ms: numOrNull(formState.latencyThreshold),
        notification_channel: formState.notificationChannel,
        notify_connection_failures: formState.alertTypes.connectionFailures,
        notify_rate_limit_warnings: formState.alertTypes.rateLimitWarnings,
        notify_high_latency_alerts: formState.alertTypes.highLatencyAlerts,
        notify_authentication_errors: formState.alertTypes.authenticationErrors,
        notify_data_validation_errors:
          formState.alertTypes.dataValidationErrors,
        weekly_usage_summary: formState.alertTypes.weeklyUsageSummary,
        // Preserve enabled-state on edit (don't silently re-enable a disabled one)
        is_enabled: existingIntegration ? existingIntegration.is_enabled : true
      };

      // If integration exists (Connected), use PATCH to update
      // If no integration exists (Not Connected), use POST to create
      const response = isEditing
        ? await updateIntegration(existingIntegration.id, integrationData)
        : await createIntegration(integrationData);

      if (response.success) {
        // Success - close modal and optionally refresh data
        onClose(true); // Pass true to indicate success
      } else {
        setError(
          response.message ||
            `Failed to ${isEditing ? "update" : "create"} integration`
        );
      }
    } catch (err) {
      console.error("Integration error:", err);
      // Try to extract more detailed error information
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        `An error occurred while ${isEditing ? "updating" : "creating"} the integration`;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const renderAuthenticationContent = () => {
    switch (formState.connectionMethod) {
      case "apikey":
        return (
          <ApiKeyConnection
            apiKey={formState.apiKey}
            setApiKey={formState.setApiKey}
            environment={formState.environment}
            setEnvironment={formState.setEnvironment}
            region={formState.region}
            setRegion={formState.setRegion}
            apiEndpoint={formState.apiEndpoint}
            setApiEndpoint={formState.setApiEndpoint}
            integrationName={integration.name}
          />
        );
      case "mcp":
        return (
          <McpConnection
            mcpServerUrl={formState.mcpServerUrl}
            setMcpServerUrl={formState.setMcpServerUrl}
            protocolVersion={formState.protocolVersion}
            setProtocolVersion={formState.setProtocolVersion}
            timeout={formState.timeout}
            setTimeout={formState.setTimeout}
            authToken={formState.authToken}
            setAuthToken={formState.setAuthToken}
            permissions={formState.permissions}
            setPermissions={formState.setPermissions}
          />
        );
      case "oauth":
        return (
          <OAuthConnection
            credentialKeys={formState.oauthCredentialKeys}
            credentials={formState.oauthCredentials}
            onCredentialChange={formState.setOauthCredential}
            isEditing={isEditing}
          />
        );
      case "webhook":
        return (
          <WebhookConnection
            webhookUrl={formState.webhookUrl}
            eventTypes={formState.eventTypes}
            setEventTypes={formState.setEventTypes}
            retryPolicy={formState.retryPolicy}
            setRetryPolicy={formState.setRetryPolicy}
          />
        );
      case "noauth":
        return (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
            This API requires{" "}
            <span className="font-semibold text-gray-900">no authentication</span>.
            No credentials are needed — adjust Settings &amp; Monitoring as desired.
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <ConfigurationModalHeader
          service={service}
          integration={integration}
          onClose={onClose}
        />

        {/* Tabs */}
        <ConfigurationTabs
          tabs={TABS}
          activeTab={formState.activeTab}
          setActiveTab={formState.setActiveTab}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {formState.activeTab === "authentication" && (
            <div className="space-y-6">
              <ConnectionMethodSelector
                methods={CONNECTION_METHODS}
                selectedMethod={formState.connectionMethod}
                onSelect={formState.setConnectionMethod}
                supportedAuthTypes={supportedAuthTypes}
                supportedAuthType={supportedAuthType}
              />
              {renderAuthenticationContent()}
            </div>
          )}

          {formState.activeTab === "settings" && (
            <SettingsTab
              requestTimeout={formState.requestTimeout}
              setRequestTimeout={formState.setRequestTimeout}
              rateLimit={formState.rateLimit}
              setRateLimit={formState.setRateLimit}
              maxRetries={formState.maxRetries}
              setMaxRetries={formState.setMaxRetries}
              cacheTtl={formState.cacheTtl}
              setCacheTtl={formState.setCacheTtl}
              enableCaching={formState.enableCaching}
              setEnableCaching={formState.setEnableCaching}
              logRequests={formState.logRequests}
              setLogRequests={formState.setLogRequests}
              enableCompression={formState.enableCompression}
              setEnableCompression={formState.setEnableCompression}
              sslVerification={formState.sslVerification}
              setSslVerification={formState.setSslVerification}
              customHeaders={formState.customHeaders}
              setCustomHeaders={formState.setCustomHeaders}
            />
          )}

          {formState.activeTab === "monitoring" && (
            <MonitoringTab
              errorThreshold={formState.errorThreshold}
              setErrorThreshold={formState.setErrorThreshold}
              latencyThreshold={formState.latencyThreshold}
              setLatencyThreshold={formState.setLatencyThreshold}
              notificationChannel={formState.notificationChannel}
              setNotificationChannel={formState.setNotificationChannel}
              alertTypes={formState.alertTypes}
              setAlertTypes={formState.setAlertTypes}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
          <div className="flex gap-3">
            <button
              onClick={() => onClose(false)}
              disabled={loading}
              className="flex-1 px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConnect}
              disabled={loading}
              className="flex-1 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isEditing ? "Updating..." : "Connecting..."}
                </>
              ) : isEditing ? (
                "Update Service"
              ) : (
                "Connect Service"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
